import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Container, Card, Button, Row, Col, Image, Badge } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../AuthContext';
import '../styles/pages/Account.css';
import { FaTrash, FaSignOutAlt, FaUserCircle, FaDollarSign, FaCog, FaPlane, FaUser, FaClipboardList } from 'react-icons/fa';
import Logout from '../components/user/Logout';
import useApi from '../Api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { QuestionnaireProvider } from '../components/questionnaire/QuestionnaireContext';
import ProfileTravelPreferences from '../components/profile/ProfileTravelPreferences';
import Popup from '../components/common/popup/Popup';
import DeleteAccountPopup from '../components/user/DeleteAccountPopup';
import SubscriptionPopup from '../components/user/SubscriptionPopup';
import VerifyEmail from '../components/user/VerifyEmail';
import UsernameModal from '../components/user/UsernameModal';
import { usePlanSelection } from '../components/user/PlanSelectionContext';
import usePlanSubscribe from '../components/user/usePlanSubscribe';
import ConsentPopup from '../components/user/ConsentPopup';

const formatDate = dateStr => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const day = date.getDate();
  let suffix = 'th';
  if (day === 1 || day === 21 || day === 31) suffix = 'st';
  else if (day === 2 || day === 22) suffix = 'nd';
  else if (day === 3 || day === 23) suffix = 'rd';
  const month = date.toLocaleString('default', { month: 'long' });
  const year = date.getFullYear();
  return `${day}${suffix} ${month} ${year}`;
};

const computeFontSize = name => {
  const baseSize = 2.5;
  if (!name) return `${baseSize}rem`;
  const extraChars = name.length - 15;
  if (extraChars > 0) {
    const newSize = Math.max(1.5, baseSize - extraChars * 0.05);
    return `${newSize}rem`;
  }
  return `${baseSize}rem`;
};

const Account = () => {
  const { user, role, logout, token } = useAuth();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [subModalTitle, setSubModalTitle] = useState('');
  const [subModalMessage, setSubModalMessage] = useState('');
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [showVerifyEmailPopup, setShowVerifyEmailPopup] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);

  const navigate = useNavigate();
  const apiClient = useApi();
  const preferencesRef = useRef(null);
  const { open: openPlanSelection } = usePlanSelection();
  const { subscribe } = usePlanSubscribe();

  const handleDeleteAccount = useCallback(async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      const response = await apiClient.delete('/delete');
      if (response.status === 200) {
        toast.success('Account successfully deleted.', {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        await logout();
        navigate('/');
      } else {
        toast.error('Failed to delete account. Please try again later.', {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      }
    } catch {
      toast.error('Failed to delete account. Please try again later.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }, [apiClient, isDeleting, logout, navigate]);

  const confirmDeleteAccount = () => {
    if (
      user?.subscription &&
      (user.subscription.status === 'active' || user.subscription.status === 'trialing') &&
      !user.subscription.cancel_at_period_end
    ) {
      setShowSubModal(true);
      setSubModalTitle('Active Subscription');
      setSubModalMessage('Please cancel your active subscription before deleting your account.');
    } else {
      setShowDeleteModal(true);
    }
  };

  const handleAdminClick = () => {
    navigate('/admin');
  };

  const handleUnsubscribe = async () => {
    try {
      const response = await apiClient.post(
        '/stripe/create-portal-session',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.portal_url) window.location.href = response.data.portal_url;
    } catch {
      toast.error('Unable to cancel subscription.', {
        position: 'top-center',
        autoClose: 3000
      });
    }
  };

  const handleSubscriptionClick = () => {
    if (user?.isPremium || user?.isSupport) {
      handleUnsubscribe();
    } else {
      openPlanSelection(plan => subscribe(plan));
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('success')) {
      setShowSubModal(true);
      setSubModalTitle('Subscription Successful');
      setSubModalMessage('Thank you for subscribing.');
    } else if (params.has('canceled')) {
      setShowSubModal(true);
      setSubModalTitle('Subscription Canceled');
      setSubModalMessage('You canceled the subscription process.');
    } else if (params.has('failed')) {
      setShowSubModal(true);
      setSubModalTitle('Payment Failed');
      setSubModalMessage('Your payment could not be completed.');
    }
  }, []);

  let subscriptionStatusLabel = 'Inactive';
  let subscriptionButton = 'Subscribe';
  let subscriptionPlanLabel = '';

  if (user?.isPremium) subscriptionPlanLabel = 'Premium';
  else if (user?.isSupport) subscriptionPlanLabel = 'Support';

  if (user?.isPremium || user?.isSupport) {
    if (
      user?.subscription?.cancel_at_period_end &&
      user?.subscription?.current_period_end &&
      new Date(user.subscription.current_period_end) > new Date()
    ) {
      subscriptionStatusLabel = `Expires on ${formatDate(user.subscription.current_period_end)}`;
      subscriptionButton = 'Manage Subscription';
    } else {
      subscriptionStatusLabel = 'Active';
      subscriptionButton = 'Manage Subscription';
    }
  }

  return (
    <Container className="account-container">
      <Helmet>
        <title>Account - WeiFinders</title>
        <meta name="description" content="Manage your WeiFinders account, update travel preferences, and view your subscription status." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://weifinders.com/account" />
      </Helmet>
      <main role="main">
        <Card className="account-card">
          <Card.Body>
            <Row className="align-items-center mb-4">
              <Col xs={12} md={4} className="text-center mb-3 mb-md-0">
                {user?.avatar ? (
                  <Image src={user.avatar} roundedCircle className="account-avatar" alt="Account Avatar" />
                ) : (
                  <FaUserCircle className="account-default-avatar" aria-label="Default Avatar" />
                )}
              </Col>
              <Col xs={12} md={8}>
                <h2
                  className="account-name"
                  style={{
                    fontSize: computeFontSize(user?.username),
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {user?.username || 'Unknown'}
                </h2>
                <p className="account-email">{user?.email}</p>
                {user && user.eligible_for_trial && !user.isPremium && (
                  <Badge bg="info" className="mb-3">
                    7-Day Free Trial Available
                  </Badge>
                )}
              </Col>
            </Row>
            <div className="info-bar-container">
              <Row className="info-bar">
                <Col xs className="info-item">
                  <span className="info-label">Subscription Status:</span>
                  {subscriptionStatusLabel === 'Inactive' ? (
                    <Badge bg="danger" className="info-badge">
                      {subscriptionStatusLabel}
                    </Badge>
                  ) : subscriptionStatusLabel === 'Active' ? (
                    <Badge bg="success" className="info-badge">
                      {subscriptionStatusLabel}
                    </Badge>
                  ) : subscriptionStatusLabel.startsWith('Expires on') ? (
                    <Badge bg="warning" className="info-badge">
                      {subscriptionStatusLabel}
                    </Badge>
                  ) : (
                    subscriptionStatusLabel
                  )}
                </Col>
                {subscriptionPlanLabel && (
                  <Col xs className="info-item">
                    <span className="info-label">Plan Type:</span>
                    <Badge bg="info" className="info-badge">
                      {subscriptionPlanLabel}
                    </Badge>
                  </Col>
                )}
                <Col xs className="info-item">
                  <span className="info-label">Email Verification:</span>
                  {user?.emailVerified ? (
                    <Badge bg="success" className="info-badge">
                      Verified
                    </Badge>
                  ) : (
                    <Badge
                      bg="danger"
                      className="info-badge"
                      onClick={() => setShowVerifyEmailPopup(true)}
                      style={{ cursor: 'pointer' }}
                    >
                      Not Verified
                    </Badge>
                  )}
                </Col>
              </Row>
            </div>
            <Row className="account-buttons-row">
              <Col xs={12} md={4} className="button-column">
                <div className="button-container">
                  <Button
                    variant="info"
                    className="account-button account-button-info"
                    onClick={() => setShowPreferencesModal(true)}
                    disabled={isDeleting}
                  >
                    Travel Preferences
                    <FaPlane className="button-icon" />
                  </Button>
                  <Button
                    variant="primary"
                    className="account-button account-button-primary"
                    onClick={handleSubscriptionClick}
                    disabled={isDeleting}
                  >
                    {subscriptionButton}
                    <FaDollarSign className="button-icon" />
                  </Button>
                  <Button
                    variant="outline-primary"
                    className="account-button"
                    onClick={() => setShowUsernameModal(true)}
                    disabled={isDeleting}
                  >
                    Edit Username
                    <FaUser className="button-icon" />
                  </Button>
                  <Button
                    variant="secondary"
                    className="account-button account-button-secondary"
                    onClick={() => setShowLogoutModal(true)}
                    disabled={isDeleting}
                  >
                    Logout
                    <FaSignOutAlt className="button-icon" />
                  </Button>
                  {role === 'admin' && (
                    <Button
                      variant="warning"
                      className="account-button account-button-warning"
                      onClick={handleAdminClick}
                      disabled={isDeleting}
                    >
                      Admin
                      <FaCog className="button-icon" />
                    </Button>
                  )}
                  <Button
                    variant="outline-info"
                    className="account-button"
                    onClick={() => setShowConsentModal(true)}
                    disabled={isDeleting}
                  >
                    Marketing Consent
                    <FaClipboardList className="button-icon" />
                  </Button>
                  <Button
                    variant="danger"
                    className="account-button account-button-danger"
                    onClick={confirmDeleteAccount}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete Account'}
                    <FaTrash className="button-icon" />
                  </Button>
                </div>
              </Col>
              <Col xs={12} md={8} />
            </Row>
          </Card.Body>
        </Card>
      </main>
      <Logout show={showLogoutModal} handleClose={() => setShowLogoutModal(false)} />
      <DeleteAccountPopup
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        isDeleting={isDeleting}
      />
      <SubscriptionPopup
        show={showSubModal}
        onClose={() => setShowSubModal(false)}
        title={subModalTitle}
        message={subModalMessage}
      />
      <ConsentPopup
        show={showConsentModal}
        onClose={() => setShowConsentModal(false)}
      />
      {showPreferencesModal && (
        <Popup
          title="Travel Preferences"
          onCancel={() => preferencesRef.current?.handleClose()}
          className="profile-travel-preferences-popup"
        >
          <div style={{ maxHeight: '80vh', overflowY: 'auto' }}>
            <QuestionnaireProvider>
              <ProfileTravelPreferences ref={preferencesRef} onClose={() => setShowPreferencesModal(false)} isMobile={false} />
            </QuestionnaireProvider>
          </div>
        </Popup>
      )}
      {showVerifyEmailPopup && (
        <VerifyEmail show={showVerifyEmailPopup} handleClose={() => setShowVerifyEmailPopup(false)} />
      )}
      <UsernameModal
        show={showUsernameModal}
        handleClose={() => setShowUsernameModal(false)}
        editMode
        initialUsername={user?.username || ''}
      />
    </Container>
  );
};

export default Account;
